import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Button } from '@mui/material';
import { notification, Select } from 'antd';
import { SaveFilled } from '@ant-design/icons';
import { creditRiskHttp } from '@network';
import { CreditRisk } from '@types';
import { ErrorIcon, OkIcon } from '@pages/credit-risk/RiskGeneral';

import cls from './FinancialStatement.module.css';

const SaveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
    <g clipPath="url(#clip0_3965_24563)">
      <path d="M14.8964 2.25077L13.2321 0.60257C13.1643 0.538906 13.075 0.5 12.9786 0.5H2.25C2.21429 0.5 2.18571 0.5 2.15 0.5C2.15 0.5 2.14643 0.5 2.14286 0.5H0.357143C0.160714 0.5 0 0.659161 0 0.85369V15.1463C0 15.3408 0.160714 15.5 0.357143 15.5H14.6429C14.8393 15.5 15 15.3408 15 15.1463V2.50189C15 2.40639 14.9607 2.31797 14.8964 2.25077ZM7.5 12.3486C6.11786 12.3486 5 11.2416 5 9.87279C5 8.50401 6.11786 7.39696 7.5 7.39696C8.88214 7.39696 10 8.50401 10 9.87279C10 11.2416 8.88214 12.3486 7.5 12.3486ZM11.4286 4.40474C11.4286 4.99187 10.95 5.46581 10.3571 5.46581H4.64286C4.05 5.46581 3.57143 4.99187 3.57143 4.40474V1.90769C3.57143 1.72377 3.72143 1.57522 3.90714 1.57522L7.91071 1.56814C8.07857 1.56814 8.21429 1.70255 8.21429 1.86878V3.97324C8.21429 4.20667 8.40357 4.39413 8.63929 4.39413H9.575C9.81071 4.39413 10 4.20667 10 3.97324V1.86171C10 1.69547 10.1357 1.56107 10.3036 1.56107H11.0929C11.2786 1.56107 11.4286 1.70962 11.4286 1.89354V4.40474Z" fill="#E1E5ED" />
    </g>
    <defs>
      <clipPath id="clip0_3965_24563">
        <rect width="15" height="15" fill="white" transform="translate(0 0.5)" />
      </clipPath>
    </defs>
  </svg>
);

const START_YEAR = 2020;

const yearOptions = Array.from(
  {
    length: new Date().getFullYear() - START_YEAR + 1,
  },
  (_, i) => ({
    label: String(START_YEAR + i),
    value: START_YEAR + i,
  }),
).reverse();

const monthOptions = Array.from({ length: 12 }, (_, i) => {
  const month = i + 1;
  const date = new Date(START_YEAR, i, 1);

  return {
    label: date.toLocaleString('en', { month: 'long' }),
    value: month,
  };
});

const financialStatusOptions = [
  {
    label: 'Audited',
    value: 'audited',
  },
  {
    label: 'Internal',
    value: 'internal',
  },
];

const INCOME_NOT_EDITABLE_ROWS = [
  'grossProfit',
  'operatingProfit',
  'ebitda',
  'overhead',
  'profitBeforeTaxes',
  'netProfit',
];

const INCOME_PERCENT_DIVIDED_BY_SALES = [
  'grossProfit',
  'costOfSales',
  'administrativeExpenses',
  'salesExpenses',
  'deprecation',
  'operatingProfit',
  'ebitda',
  'overhead',
  'financialExpenses',
  'financialIncome',
  'foreignExchange',
  'otherExpenses',
  'bankFees',
  'extraordinaryItems',
  'otherIncome',
  'profitBeforeTaxes',
  'taxesAndProfit',
  'netProfit',
];

const initialFinancialState = {
  incomeStatement: {
    periods: [
      { year: 2024, month: 2, status: 'internal' },
      { year: 2025, month: 2, status: 'internal' },
      { year: 2026, month: 2, status: 'internal' },
    ],
    sales: {
      label: 'Sales',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    costOfSales: {
      label: 'Cost of Sales',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    grossProfit: {
      label: 'Gross Profit',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    administrativeExpenses: {
      label: 'Administrative Expenses',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    salesExpenses: {
      label: 'Sales Expenses',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    deprecation: {
      label: 'Deprecation',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    operatingExpenses: {
      label: 'Operating Expenses',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    operatingProfit: {
      label: 'Operating Profit',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    ebitda: {
      label: 'EBITDA',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    financialExpenses: {
      label: 'Financial Expenses',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    financialIncome: {
      label: 'Financial Income',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    foreignExchange: {
      label: 'Foreign Exchange',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    otherExpenses: {
      label: 'Other Expenses',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    bankFees: {
      label: 'Bank Fees',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    overhead: {
      label: 'Overhead',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    extraordinaryItems: {
      label: 'Extraordinary Items',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    otherIncome: {
      label: 'Other Income',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    profitBeforeTaxes: {
      label: 'Profit Before Taxes',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    taxesAndProfit: {
      label: 'Taxes and Profit',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
    netProfit: {
      label: 'Net Profit',
      cols: [
        { values: [0, 0] },
        { values: [0, 0] },
        { values: [0, 0] },
      ],
    },
  },
  sections: {
    assets: {
      label: 'Assets',
      total: {
        label: 'Total Assets',
      },
      groups: [
        {
          total: {
            label: 'Total Current Assets',
          },
          rows: [
            'Banks and Investments',
            'Customers',
            'Inventory',
            'Other Debtors',
            'Advances to Suppliers',
            'Other Assets (Related Parties)',
            'Taxes Receivable',
          ],
          data: [
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
          ],
        },
        {
          total: {
            label: 'Total Fixed Assets',
          },
          rows: [
            'Property, Plant and Equipment',
            'Accumulated Depreciation',
            'Other Assets (Permanent Invest. in Affiliated Companies)',
          ],
          data: [
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
          ],
        },
        {
          total: {
            label: 'Total Deferred Assets',
          },
          rows: [
            'Prepaid Expenses (Installation Costs - Amortization)',
            'Other Assets',
            'Security Deposits',
          ],
          data: [
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
          ],
        },
      ],
    },
    liabilities: {
      label: 'Liabilities',
      total: {
        label: 'Total Liabilities',
      },
      groups: [
        {
          total: {
            label: 'Total Current Liabilities',
          },
          rows: [
            'Taxes Payable',
            'Suppliers',
            'Advances to Customers',
            'Other Creditors',
            'Other Liabilities (Related Parties)',
            'Liquid Guarantees',
            'Current Financial Liabilities',
            'Current Portion of Long-Term Debt',
          ],
          data: [
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
          ],
        },
        {
          total: {
            label: 'Total Long-Term Liabilities',
          },
          rows: [
            'Long Term Financial Liabilities',
            'Other Liabilities',
          ],
          data: [
            {
              values: [
                [0, 0],
                [0, 0],
              ],
            },
            {
              values: [
                [0, 0],
                [0, 0],
              ],
            },
            {
              values: [
                [0, 0],
                [0, 0],
              ],
            },
          ],
        },
      ],
    },
    capital: {
      label: 'Capital',
      total: {
        label: 'Total Liabilities + Equity',
      },
      groups: [
        {
          total: {
            label: 'Shareholders’ Equity',
          },
          rows: [
            'Share Capital',
            'Other Capital Accounts',
            'Contributions Received',
            'Retained Earnings (Legal Reserve)',
            'Accumulated Profits',
            'Current Year Profit',
          ],
          data: [
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
            {
              values: [
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
                [0, 0],
              ],
            },
          ],
        },
      ],
    },
  },
};

type EditingCell =
  | {
      source: 'income';
      rowKey: string;
      colIndex: number;
    }
  | {
      source: 'section';
      sectionKey: string;
      groupIndex: number;
      colIndex: number;
      rowIndex: number;
    };

interface Props {
  riskId: CreditRisk['id'];
  financialStatement?: any;
  close: () => void;
  onSave?: (balanceSheet: any) => void;
}

const getRowValue = (row: [number, number] | number[], index: 0 | 1): number => {
  const val = row[index];
  return typeof val === 'number' ? val : 0;
};

const sumColValuesForBalance = (
  groups: Array<{ data: Array<{ values: Array<[number, number] | number[]> }> }>,
  colIndex: number,
) =>
  groups.reduce((acc, group) => {
    const groupSum = (group.data[colIndex]?.values ?? []).reduce<number>(
      (sum, row) => sum + getRowValue(row, 0),
      0,
    );
    return acc + groupSum;
  }, 0);

export const isFinancialStatementAllDone = (balanceSheet: any): boolean => {
  if (balanceSheet == null) return false;
  let data: any;
  try {
    data = typeof balanceSheet === 'string' ? JSON.parse(balanceSheet) : balanceSheet;
  } catch {
    return false;
  }
  const sections = data?.sections;
  if (!sections?.assets || !sections?.liabilities || !sections?.capital) return false;
  const numPeriods = data?.incomeStatement?.periods?.length ?? 0;
  if (numPeriods === 0) return false;
  for (let colIndex = 0; colIndex < numPeriods; colIndex++) {
    const totalCurrentAssets = sumColValuesForBalance(
      sections.assets.groups.slice(0, 1), colIndex,
    );
    const totalFixedAssets = sumColValuesForBalance(
      sections.assets.groups.slice(1, 2), colIndex,
    );
    const totalDeferredAssets = sumColValuesForBalance(
      sections.assets.groups.slice(2, 3), colIndex,
    );
    const totalCurrentLiabilities = sumColValuesForBalance(
      sections.liabilities.groups.slice(0, 1), colIndex,
    );
    const totalLongTermLiabilities = sumColValuesForBalance(
      sections.liabilities.groups.slice(1, 2), colIndex,
    );
    const shareholdersEquity = sumColValuesForBalance(
      sections.capital.groups, colIndex,
    );
    const balance =
      totalCurrentAssets +
      totalFixedAssets +
      totalDeferredAssets -
      totalCurrentLiabilities -
      totalLongTermLiabilities -
      shareholdersEquity;
    if (balance <= 0) return false;
  }
  return true;
};

export const FinancialStatement = ({ riskId, financialStatement, close, onSave }: Props) => {
  const [balanceSheet, setBalanceSheet] = useState<any>(
    financialStatement || initialFinancialState,
  );
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editingValue, setEditingValue] = useState('');

  const renderCellValue = (value: number) => {
    const formatted = new Intl.NumberFormat('en-EN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    }).format(Math.abs(value));
    return value < 0 ? `(${formatted})` : formatted;
  };

  const renderCellPercent = (value: number) => {
    const percentFormatter = new Intl.NumberFormat('en-EN', {
      style: 'percent',
    });

    const num = value < 0 ? Math.abs(value) : value || 0;
    const formatted = percentFormatter.format(num);

    return value < 0 
      ? `(${formatted})` 
      : formatted;
  };

  const isNegativeNumber = (value: unknown) => typeof value === 'number' && value < 0;

  const handleIncomeValueChange = (
    rowKey: string,
    colIndex: number,
    newValue: number,
  ) => {
    setBalanceSheet(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const row = next.incomeStatement?.[rowKey];
      if (!row?.cols?.[colIndex]) return prev;
      row.cols[colIndex].values[0] = newValue;
      return next;
    });
  };

  const handlePeriodChange = (
    periodIndex: number,
    field: 'status' | 'month' | 'year',
    value: string | number,
  ) => {
    setBalanceSheet(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const periods = next.incomeStatement?.periods;
      if (!periods?.[periodIndex]) return prev;
      periods[periodIndex][field] = value;
      return next;
    });
  };

  const handleValueChange = (
    sectionKey: string,
    groupIndex: number,
    colIndex: number,
    rowIndex: number,
    newValue: number,
  ) => {
    setBalanceSheet(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const section = next.sections[sectionKey];
      if (!section?.groups?.[groupIndex]?.data?.[colIndex]) return prev;
      section.groups[groupIndex].data[colIndex].values[rowIndex][0] = newValue;
      return next;
    });
  };

  const parseInputValue = (input: string): number => {
    const trimmed = input.trim();
    const isNegativeInParens = /^\([^)]*\)$/.test(trimmed);
    const cleaned = trimmed.replace(/[(),\s]/g, '');
    const num = parseFloat(cleaned || '0');
    if (isNaN(num)) return 0;
    return isNegativeInParens ? -Math.abs(num) : num;
  };

  const getRowValue = (row: [number, number] | number[], index: 0 | 1): number => {
    const val = row[index];
    return typeof val === 'number' ? val : 0;
  };

  const sumColValues = (
    groups: Array<{
      data: Array<{ values: Array<[number, number] | number[]> }>;
    }>,
    colIndex: number,
    valueIndex: 0 | 1,
  ) =>
    groups.reduce(
      (acc, group) => {
        const groupSum = group.data[colIndex].values.reduce<number>(
          (sum, row) => sum + getRowValue(row, valueIndex),
          0,
        );
        return acc + groupSum;
      },
      0,
    );

  const getIsDoneForPeriod = (colIndex: number): boolean => {
    const sections = balanceSheet?.sections;
    if (!sections?.assets || !sections?.liabilities || !sections?.capital) return false;
    const totalCurrentAssets = sumColValues(
      sections.assets.groups.slice(0, 1), colIndex, 0,
    );
    const totalFixedAssets = sumColValues(
      sections.assets.groups.slice(1, 2), colIndex, 0,
    );
    const totalDeferredAssets = sumColValues(
      sections.assets.groups.slice(2, 3), colIndex, 0,
    );
    const totalCurrentLiabilities = sumColValues(
      sections.liabilities.groups.slice(0, 1), colIndex, 0,
    );
    const totalLongTermLiabilities = sumColValues(
      sections.liabilities.groups.slice(1, 2), colIndex, 0,
    );
    const shareholdersEquity = sumColValues(
      sections.capital.groups, colIndex, 0,
    );
    const balance =
      totalCurrentAssets +
      totalFixedAssets +
      totalDeferredAssets -
      totalCurrentLiabilities -
      totalLongTermLiabilities -
      shareholdersEquity;
    return balance > 0;
  };

  const getIncomeRowValue = (rowKey: string, colIndex: number): number => {
    const income = balanceSheet.incomeStatement;
    if (rowKey === 'grossProfit') {
      const sales = income.sales?.cols?.[colIndex]?.values[0] ?? 0;
      const costOfSales = income.costOfSales?.cols?.[colIndex]?.values[0] ?? 0;
      return sales - costOfSales;
    }
    if (rowKey === 'operatingExpenses') {
      const administrative =
        income.administrativeExpenses?.cols?.[colIndex]?.values[0] ?? 0;
      const salesExp = income.salesExpenses?.cols?.[colIndex]?.values[0] ?? 0;
      const deprecation = income.deprecation?.cols?.[colIndex]?.values[0] ?? 0;
      return administrative + salesExp + deprecation;
    }
    if (rowKey === 'operatingProfit') {
      const grossProfit = getIncomeRowValue('grossProfit', colIndex);
      const operatingExp = getIncomeRowValue('operatingExpenses', colIndex);
      return grossProfit - operatingExp;
    }
    if (rowKey === 'ebitda') {
      const operatingProfit = getIncomeRowValue('operatingProfit', colIndex);
      const deprecation = income.deprecation?.cols?.[colIndex]?.values[0] ?? 0;
      return operatingProfit + deprecation;
    }
    if (rowKey === 'overhead') {
      const financialExpenses =
        income.financialExpenses?.cols?.[colIndex]?.values[0] ?? 0;
      const financialIncome =
        income.financialIncome?.cols?.[colIndex]?.values[0] ?? 0;
      const foreignExchange =
        income.foreignExchange?.cols?.[colIndex]?.values[0] ?? 0;
      const otherExpenses =
        income.otherExpenses?.cols?.[colIndex]?.values[0] ?? 0;
      const bankFees = income.bankFees?.cols?.[colIndex]?.values[0] ?? 0;
      return (
        financialExpenses +
        financialIncome +
        foreignExchange +
        otherExpenses +
        bankFees
      );
    }
    if (rowKey === 'profitBeforeTaxes') {
      const operatingProfit = getIncomeRowValue('operatingProfit', colIndex);
      const overheadVal = getIncomeRowValue('overhead', colIndex);
      const extraordinaryItems =
        income.extraordinaryItems?.cols?.[colIndex]?.values[0] ?? 0;
      const otherIncome = income.otherIncome?.cols?.[colIndex]?.values[0] ?? 0;
      return (
        operatingProfit + overheadVal + extraordinaryItems + otherIncome
      );
    }
    if (rowKey === 'netProfit') {
      const profitBeforeTaxes =
        getIncomeRowValue('profitBeforeTaxes', colIndex);
      const taxesAndProfit =
        income.taxesAndProfit?.cols?.[colIndex]?.values[0] ?? 0;
      return profitBeforeTaxes + taxesAndProfit;
    }
    return income[rowKey]?.cols?.[colIndex]?.values[0] ?? 0;
  };

  const incomeTotalsByCol = (() => {
    const rowKeys = Object.keys(balanceSheet.incomeStatement);
    const numCols =
      balanceSheet.incomeStatement[rowKeys[0]]?.cols?.length ?? 0;
    return Array.from({ length: numCols }, (_, colIndex) =>
      rowKeys.reduce(
        (sum, rowKey) => sum + getIncomeRowValue(rowKey, colIndex),
        0,
      ),
    );
  })();

  const submit = async () => {
    try {
      await creditRiskHttp.updateFinancialStatement(riskId, JSON.stringify(balanceSheet));
      notification.success({ message: 'Financial statement updated successfully' });
      onSave?.(balanceSheet);
    } catch (error) {
      console.error('Error updating financial statement:', error);
    } finally {
      close();
    }
  };

  return (
    <div className={cls._}>
      <Button className={cls.saveButton} onClick={submit}>
        <SaveIcon />
      </Button>

      <div className={cls.title}>Financial statement</div>
      <div className={cls.tableTitle}>
        <div>Income Statement</div>
        <div>MXP (Thousands of Pesos)</div>
      </div>
      <div className={cls.income}>
        <div className={cls.periodsSection}>
          <div className={cls.space} />
          <div className={cls.periodsValues}>
            {(balanceSheet.incomeStatement.periods ?? []).map((period, periodIndex) => (
              <div key={periodIndex} className={cls.period}>
                <div className={cls.periodValue}>
                  <Select
                    options={financialStatusOptions}
                    placeholder="Status"
                    value={period.status}
                    onChange={value =>
                      handlePeriodChange(periodIndex, 'status', value)
                    }
                  />
                </div>
                <div className={cls.periodValue}>
                  <Select
                    options={monthOptions}
                    placeholder="Month"
                    value={period.month}
                    onChange={value =>
                      handlePeriodChange(periodIndex, 'month', value)
                    }
                  />
                </div>
                <div className={cls.periodValue}>
                  <Select
                    options={yearOptions}
                    placeholder="Year"
                    value={period.year}
                    onChange={value =>
                      handlePeriodChange(periodIndex, 'year', value)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={cls.head}>
          <div className={cls.labels}>No. Of Months</div>
          <div className={cls.sections}>
            <div className={cls.section}>12 Months</div>
            <div className={cls.section}>12 Months</div>
            <div className={cls.section}>3 Months</div>
          </div>
        </div>
        {Object.entries(balanceSheet.incomeStatement)
          .filter(([key]) => key !== 'periods')
          .map(([key, row]) => {
            const rowData = row as { label: string; cols: { values: number[] }[] };
            const cols = rowData.cols ?? [];
            return (
              <div
                key={key}
                className={clsx(cls.group, {
                  [cls.notEditable]: INCOME_NOT_EDITABLE_ROWS.includes(key),
                })}>
                <div className={cls.labels}>
                  <div
                    key={key}
                    className={clsx(cls.label, {
                      [cls.notEditable]: INCOME_NOT_EDITABLE_ROWS.includes(key),
                    })}>
                    {rowData.label}
                  </div>
                </div>
                <div className={cls.sections}>
                  {cols.map((col, colIndex) => (
                    <div key={colIndex} className={cls.section}>
                      <div
                        key={`${key}-total-${colIndex}`}
                        className={clsx(cls.col, {
                          [cls.notEditable]: INCOME_NOT_EDITABLE_ROWS.includes(key),
                        })}>
                        <div className={clsx(cls.cell, {
                          [cls.notEditable]: INCOME_NOT_EDITABLE_ROWS.includes(key),
                          [cls.isEditable]:
                          key !== 'grossProfit' &&
                          key !== 'operatingExpenses' &&
                          key !== 'operatingProfit' &&
                          key !== 'ebitda' &&
                          key !== 'overhead' &&
                          key !== 'profitBeforeTaxes' &&
                          key !== 'netProfit',
                          [cls.negativeValue]: isNegativeNumber(
                            getIncomeRowValue(key, colIndex),
                          ),
                        })}>
                          {key === 'grossProfit' ||
                        key === 'operatingExpenses' ||
                        key === 'operatingProfit' ||
                        key === 'ebitda' ||
                        key === 'overhead' ||
                        key === 'profitBeforeTaxes' ||
                        key === 'netProfit' ? (
                              <span>
                                {renderCellValue(getIncomeRowValue(key, colIndex))}
                              </span>
                            ) : (
                              <input
                                type="text"
                                value={
                                  editingCell?.source === 'income' &&
                              editingCell?.rowKey === key &&
                              editingCell?.colIndex === colIndex
                                    ? editingValue
                                    : renderCellValue(col.values[0])
                                }
                                onFocus={() => {
                                  setEditingCell({
                                    source: 'income',
                                    rowKey: key,
                                    colIndex,
                                  });
                                  setEditingValue(
                                    col.values[0] === 0
                                      ? ''
                                      : renderCellValue(col.values[0]),
                                  );
                                }}
                                onChange={e => setEditingValue(e.target.value)}
                                onBlur={e => {
                                  const parsed = parseInputValue(e.target.value);
                                  handleIncomeValueChange(key, colIndex, parsed);
                                  setEditingCell(null);
                                }}
                                className={cls.input}
                              />
                            )}
                        </div>
                        {(() => {
                          const value = getIncomeRowValue(key, colIndex);
                          const salesValue =
                          balanceSheet.incomeStatement.sales?.cols?.[colIndex]
                            ?.values[0] ?? 0;
                          const incomeTotal = incomeTotalsByCol[colIndex] ?? 0;
                          const calculatedPercent =
                          key === 'sales'
                            ? 1
                            : INCOME_PERCENT_DIVIDED_BY_SALES.includes(key)
                              ? salesValue !== 0
                                ? value / salesValue
                                : 0
                              : incomeTotal !== 0
                                ? value / incomeTotal
                                : 0;
                          return (
                            <div
                              className={clsx(cls.cell, {
                                [cls.notEditable]: INCOME_NOT_EDITABLE_ROWS.includes(key),
                                [cls.negativeValue]:
                                isNegativeNumber(calculatedPercent),
                                [cls.isHidden]: key === 'operatingExpenses',
                              })}>
                              <span>
                                {renderCellPercent(calculatedPercent)}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
      </div>

      <div className={cls.tableTitle}>
        <div>Balance Sheet</div>
        <div>MXP (Thousands of Pesos)</div>
      </div>
      <div className={cls.new}>
        <div className={cls.periodsSection}>
          <div className={cls.space} />
          <div className={cls.periodsValues}>
            {(balanceSheet.incomeStatement.periods ?? []).map((period, periodIndex) => (
              <div key={periodIndex} className={cls.period}>
                <div className={cls.periodValue}>
                  <Select
                    options={financialStatusOptions}
                    placeholder="Status"
                    value={period.status}
                    onChange={value =>
                      handlePeriodChange(periodIndex, 'status', value)
                    }
                    showArrow={false}
                    disabled
                  />
                </div>
                <div className={cls.periodValue}>
                  <Select
                    options={monthOptions}
                    placeholder="Month"
                    value={period.month}
                    onChange={value =>
                      handlePeriodChange(periodIndex, 'month', value)
                    }
                    showArrow={false}
                    disabled
                  />
                </div>
                <div className={cls.periodValue}>
                  <Select
                    options={yearOptions}
                    placeholder="Year"
                    value={period.year}
                    onChange={value =>
                      handlePeriodChange(periodIndex, 'year', value)
                    }
                    showArrow={false}
                    disabled
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {Object.entries(balanceSheet?.sections || {}).map(([key, section]: any) => {
          const sectionTotalsByCol = section.groups[0]?.data.map((_, colIndex) => {
            const sectionValue = sumColValues(section.groups, colIndex, 0);
            const isCapitalSection = key === 'capital';
            const liabilitiesSection = balanceSheet?.sections?.liabilities;
            const liabilitiesValue =
              isCapitalSection && liabilitiesSection
                ? sumColValues(liabilitiesSection.groups, colIndex, 0)
                : 0;
            return isCapitalSection
              ? sectionValue + liabilitiesValue
              : sectionValue;
          }) ?? [];

          return (
            <div key={key}>
              <div className={cls.table}>
                <div className={cls.head}>{section.label}</div>
                {section.groups.map((group, groupIndex) => (
                  <section key={`${key}-section-${groupIndex}`}>
                    <div className={cls.group}>
                      <div className={cls.labels}>
                        {group.rows.map(row => (
                          <div key={row} className={cls.label}>{row}</div>
                        ))}
                      </div>
                      <div className={cls.sections}>
                        {group.data.map((col, colIndex) => {
                          return (
                            <div key={`${key}-col-${colIndex}`} className={cls.section}>
                              {col.values.map(([value, percent], rowIndex) => {
                                const isEditable = true;
                                const sectionTotal = sectionTotalsByCol[colIndex] ?? 0;
                                const calculatedPercent = sectionTotal !== 0
                                  ? value / sectionTotal
                                  : 0;

                                return (
                                  <div key={`${key}-${colIndex}-${rowIndex}`} className={cls.block}>
                                    <div className={clsx(cls.cell, {
                                      [cls.isEditable]: isEditable,
                                      [cls.negativeValue]: isNegativeNumber(value),
                                    })}>
                                      {isEditable ? (
                                        <input
                                          type="text"
                                          value={
                                            editingCell?.source === 'section' &&
                                            editingCell?.sectionKey === key &&
                                            editingCell?.groupIndex === groupIndex &&
                                            editingCell?.colIndex === colIndex &&
                                            editingCell?.rowIndex === rowIndex
                                              ? editingValue
                                              : renderCellValue(value)
                                          }
                                          onFocus={() => {
                                            setEditingCell({
                                              source: 'section',
                                              sectionKey: key,
                                              groupIndex,
                                              colIndex,
                                              rowIndex,
                                            });
                                            setEditingValue(
                                              value === 0 ? '' : renderCellValue(value),
                                            );
                                          }}
                                          onChange={e => setEditingValue(e.target.value)}
                                          onBlur={e => {
                                            const parsed = parseInputValue(e.target.value);
                                            handleValueChange(
                                              key, groupIndex, colIndex, rowIndex, parsed,
                                            );
                                            setEditingCell(null);
                                          }}
                                          className={cls.input}
                                        />
                                      ) : (
                                        <span>
                                          {renderCellValue(value)}
                                        </span>
                                      )}
                                    </div>
                                    <div
                                      className={clsx(cls.cell, {
                                        [cls.negativeValue]:
                                        isNegativeNumber(calculatedPercent),
                                      })}>
                                      <span>
                                        {renderCellPercent(calculatedPercent)}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className={cls.total}>
                      <div className={cls.labels}>
                        <div className={cls.label}>{group.total.label}</div>
                      </div>
                      <div className={cls.sections}>
                        {group.data.map((col, colIndex) => {
                          const value = col.values.reduce(
                            (acc, row) => acc + Number(row[0] ?? 0),
                            0,
                          );
                          const sectionTotal = sectionTotalsByCol[colIndex] ?? 0;
                          const percent = sectionTotal !== 0 ? value / sectionTotal : 0;

                          return (
                            <div key={`${key}-total-${colIndex}`} className={clsx(cls.col)}>
                              <div className={clsx(cls.cell, {
                                [cls.negativeValue]: isNegativeNumber(value),
                              })}>
                                <span>{renderCellValue(value)}</span>
                              </div>
                              <div className={clsx(cls.cell, {
                                [cls.negativeValue]: isNegativeNumber(percent),
                              })}>
                                <span>{renderCellPercent(percent)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </section>
                ))}
              </div>

              <div className={cls.summary}>
                <div className={cls.labels}>
                  <div className={cls.label}>
                    {section.total.label}
                  </div>
                </div>
                <div className={cls.sections}>
                  {section.groups[0]?.data.map((_, colIndex) => {
                    const value = sectionTotalsByCol[colIndex] ?? 0;
                    const percent = value !== 0 ? 1 : 0;

                    return (
                      <div
                        key={`${key}-summary-${colIndex}`}
                        className={clsx(cls.col)}>
                        <div
                          className={clsx(cls.cell, {
                            [cls.negativeValue]: isNegativeNumber(value),
                          })}>
                          <span>{renderCellValue(value)}</span>
                        </div>
                        <div
                          className={clsx(cls.cell, {
                            [cls.negativeValue]: isNegativeNumber(percent),
                          })}>
                          <span>{renderCellPercent(percent)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className={cls.control}>
        <div className={cls.controlLabel}>Control</div>
        <div className={cls.controlValues}>
          {(balanceSheet.incomeStatement.periods ?? []).map((_, periodIndex) => (
            <div key={periodIndex} className={cls.controlValue}>
              {getIsDoneForPeriod(periodIndex) ? <OkIcon /> : <ErrorIcon />}
            </div>
          ))}
        </div>
      </div>

      <div className={cls.actions}>
        <Button
          className={clsx(cls.action, cls.actionClose)}
          variant="outlined"
          color="secondary"
          onClick={close}
        >
          Close Window
        </Button>
        <Button
          className={clsx(cls.action, cls.actionComplete)}
          variant="contained"
          color="secondary"
          startIcon={<SaveFilled className={cls.actionIcon} />}
          onClick={submit}
        >Complete and Save</Button>
      </div>
    </div>
  );
};
